/**
 * Reusable text input field component
 * @module FormFields/TextInputField
 */

import React, { memo } from 'react';
import { FormFieldWrapper } from './FormFieldWrapper';

/**
 * Props for TextInputField component
 */
export interface TextInputFieldProps {
  /** Field label */
  label: React.ReactNode;
  /** Field ID */
  id: string;
  /** Field name */
  name: string;
  /** Field value */
  value: string;
  /** Change handler */
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Field type */
  type?: 'text' | 'email' | 'tel';
  /** Max length */
  maxLength?: number;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Show helper */
  showHelper?: boolean;
  /** Field ref */
  fieldRef?: React.RefObject<HTMLInputElement>;
  /** Wrapper ref */
  wrapperRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Text input field with consistent styling
 * Combines FormFieldWrapper with a standard text input for common form fields
 * 
 * @param props - Component props
 * @param props.label - Field label
 * @param props.id - Unique field ID
 * @param props.name - Form field name
 * @param props.value - Current field value
 * @param props.onChange - Change handler function
 * @param props.placeholder - Placeholder text (optional)
 * @param props.type - Input type: 'text', 'email', or 'tel' (defaults to 'text')
 * @param props.maxLength - Maximum character length (optional)
 * @param props.error - Error message to display
 * @param props.helperText - Helper text for guidance
 * @param props.showHelper - Whether to show helper text
 * @param props.fieldRef - Ref to the input element
 * @param props.wrapperRef - Ref to the wrapper div
 * @returns TextInputField component
 */
export const TextInputField = memo<TextInputFieldProps>(({ 
  label,
  id,
  name,
  value,
  onChange,
  placeholder = '',
  type = 'text',
  maxLength,
  error,
  helperText,
  showHelper,
  fieldRef,
  wrapperRef,
}) => (
  <FormFieldWrapper
    label={label}
    id={id}
    error={error}
    helperText={helperText}
    showHelper={showHelper}
    fieldRef={wrapperRef}
  >
    <div className="w-full relative">
      <input
        ref={fieldRef}
        autoComplete="on"
        className="w-full min-w-0 block"
        style={{ width: "100%" }}
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
      />
    </div>
  </FormFieldWrapper>
));

TextInputField.displayName = 'TextInputField';
